import { useCallback, useEffect, useState } from 'react';
import type { Me } from '../api/auth';
import { unitPhotoSettingsApi } from '../api/unitPhotoSettings';
import type { UnitPhotoSettings } from '../api/unitPhotoSettings';
import { AUDIENCE_COPY, AUDIENCE_OPTIONS, audienceCopy } from '../lib/photoAudience';
import type { PhotoUploadAudience } from '../lib/photoAudience';
import { AppHeader } from '../ui/AppHeader';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { ToggleRow } from '../ui/ToggleRow';

/**
 * What the unit does about photographs — TROOP-225, §6.
 *
 * **Two settings, and the second is only asked while the first is on.** Whether there are photos at
 * all is a decision about the unit's young members; who may add them is a decision about the
 * gallery. A unit with photos off has not said anything about who would add them, so offering the
 * choice there would be collecting an answer to a question nobody has asked — and it would put a
 * stated audience on a screen where it means nothing, which is the *count in the prose that has
 * drifted* failure wearing different clothes.
 *
 * **The switch had no control anywhere in the product until this screen.** `Unit.PhotosEnabled`
 * shipped with §6 and was flipped by SQL in the smoke suite; a second setting beside a first one
 * nobody can reach would have been TROOP-167's failure twice over, so this ticket carries both.
 *
 * **Five choices, never a slider.** `AllAdults` and `AllOfficers` are incomparable — an adult plain
 * member is in one, a youth officer in the other — so any control that puts them in an order is
 * wrong for somebody. Five radio cards, widest first for reading order and for nothing else.
 *
 * **Any member may read this; adult officers change it.** That is `ManageScreen`'s own ethos —
 * *anyone can look; your officers do the changing* — and it is why this screen renders the unit's
 * answers as facts to everybody and as controls to the people the API would accept a write from.
 * Position *and* classification, because whether a unit photographs its children is an adults'
 * decision even in a unit that then hands the gallery to its youth officers. `canChange` comes from
 * the server; nothing here derives it.
 */
export function PhotoSettingsScreen({
  unitId,
  unitName,
  me,
  onNavigate,
}: {
  unitId: string;
  unitName: string;
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const [settings, setSettings] = useState<UnitPhotoSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setSettings(await unitPhotoSettingsApi.get(unitId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load what this unit does about photos.');
    }
  }, [unitId]);

  useEffect(() => {
    void load();
  }, [load]);

  // ToggleRow is optimistic and controlled, so the state it settles onto has to be this one:
  // updating here after the write resolves is what stops the row snapping back, and letting the
  // rejection through is what makes a failure visible rather than silent.
  async function setEnabled(photosEnabled: boolean) {
    setError(null);
    setSettings(await unitPhotoSettingsApi.set(unitId, { photosEnabled }));
  }

  async function setAudience(uploadAudience: PhotoUploadAudience) {
    setBusy(true);
    setError(null);

    try {
      setSettings(await unitPhotoSettingsApi.set(unitId, { uploadAudience }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That change did not save.');
      // Back to whatever the server actually holds, rather than leaving the cards showing a
      // choice that was refused.
      await load();
    } finally {
      setBusy(false);
    }
  }

  const chosen = settings ? audienceCopy(settings.uploadAudience) : null;

  return (
    <Page>
      <AppHeader me={me} currentUnitId={unitId} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p className="t-eyebrow" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              {unitName}
            </p>
            <h1
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 var(--space-3)' }}
            >
              Photos
            </h1>
            <p
              className="t-body"
              style={{ color: 'var(--color-text-secondary)', maxWidth: '44ch', margin: 0 }}
            >
              Whether this unit keeps photos of its events, and who adds them. Everyone who can see
              an event sees its photos either way — this only says who fills them.
            </p>

            <div style={{ marginTop: 'var(--space-4)' }}>
              <Button variant="secondary" onClick={() => onNavigate(`/units/${unitId}/manage`)}>
                Back to Manage
              </Button>
            </div>
          </div>
        </Gutter>
      </section>

      <Gutter>
        <div className="flex flex-col" style={{ paddingBlock: 'var(--space-6)', gap: 'var(--space-6)' }}>
          {error && (
            <p className="t-body" role="alert" style={{ color: 'var(--color-status-blocked)', margin: 0 }}>
              {error}
            </p>
          )}

          {settings === null && !error && (
            <p className="t-body" style={{ color: 'var(--color-text-secondary)' }}>
              Loading&hellip;
            </p>
          )}

          {settings && (
            <>
              <ToggleRow
                label="Keep photos of events"
                secondary="Turning this off closes every gallery in the unit. Nothing is deleted, and turning it back on brings them back."
                checked={settings.photosEnabled}
                onChange={setEnabled}
                // No `disabled` prop anywhere in this product: a greyed control owes an
                // explanation, and this is it.
                disabledReason={
                  settings.canChange
                    ? undefined
                    : 'Your unit’s adult leaders decide this.'
                }
              />

              {/* Only while photos are on. See the screen's remarks: a unit with photos off has
                  said nothing about who would add them, and rendering the choice there would be
                  putting an answer on screen to a question nobody asked. */}
              {settings.photosEnabled && (
                <section>
                  <h2
                    className="t-eyebrow"
                    style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-2)' }}
                  >
                    Who can add them
                  </h2>

                  {settings.canChange ? (
                    <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                      <legend className="sr-only">Who can add photos</legend>

                      <div className="flex flex-col" style={{ gap: 'var(--space-2)' }}>
                        {AUDIENCE_OPTIONS.map((option) => (
                          <label
                            key={option}
                            className="flex items-start gap-3"
                            style={{
                              background: 'var(--color-surface-raised)',
                              border:
                                settings.uploadAudience === option
                                  ? '1.5px solid var(--color-brand-ink)'
                                  : '1.5px solid var(--color-border-subtle)',
                              borderRadius: 'var(--radius-md)',
                              padding: 'var(--space-3) var(--space-4)',
                              minHeight: 'var(--tap-min)',
                              cursor: busy ? 'default' : 'pointer',
                            }}
                          >
                            <input
                              type="radio"
                              name="photoUploadAudience"
                              value={option}
                              checked={settings.uploadAudience === option}
                              onChange={() => void setAudience(option)}
                              disabled={busy}
                              style={{ marginTop: '4px' }}
                            />
                            <span>
                              <span
                                className="t-body-strong"
                                style={{ color: 'var(--color-text-primary)', display: 'block' }}
                              >
                                {AUDIENCE_COPY[option].label}
                              </span>
                              <span
                                className="t-caption"
                                style={{ color: 'var(--color-text-secondary)' }}
                              >
                                {AUDIENCE_COPY[option].detail}
                              </span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  ) : (
                    // A fact rather than five controls that would be refused. The unit's answer is
                    // still worth knowing to a member deciding whether to reach for their camera.
                    <div>
                      <p
                        className="t-body-strong"
                        style={{ color: 'var(--color-text-primary)', margin: 0 }}
                      >
                        {chosen?.label}
                      </p>
                      <p
                        className="t-caption"
                        style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-1) 0 0' }}
                      >
                        {chosen?.detail} Your unit&rsquo;s adult leaders decide this.
                      </p>
                    </div>
                  )}
                </section>
              )}

              <p
                className="t-caption"
                style={{ color: 'var(--color-text-muted)', maxWidth: '52ch', margin: 0 }}
              >
                Narrowing this changes who can add photos from now on. Photos already in a gallery
                stay where they are — taking one down is its own decision, and anyone can ask a
                leader to do it.
              </p>
            </>
          )}
        </div>
      </Gutter>

      <Footer />
    </Page>
  );
}
